import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  BookOpen,
  ArrowLeft,
  Send,
  Loader2,
  Sparkles,
  Brain,
  MessageCircle,
  GraduationCap,
  Heart,
  Lightbulb,
  RotateCcw,
  Download,
  MoreVertical,
  Volume2,
  VolumeX,
  Pause,
  Play
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { toast } from 'sonner';

// Helper to get translated lesson/module titles
const getTranslatedTitle = (t, type, id, fallback) => {
  const key = `${type}.${id.replace('.', '_')}`;
  const translated = t(key, { defaultValue: '' });
  return translated || fallback;
};

// Quick answer prompts - keys for translation
const QUICK_PROMPT_KEYS = {
  '0.1': ['quickPrompts.0_1_1', 'quickPrompts.0_1_2', 'quickPrompts.0_1_3'],
  '0.2': ['quickPrompts.0_2_1', 'quickPrompts.0_2_2', 'quickPrompts.0_2_3'],
  '0.3': ['quickPrompts.0_3_1', 'quickPrompts.0_3_2', 'quickPrompts.0_3_3'],
  '0.4': ['quickPrompts.0_4_1', 'quickPrompts.0_4_2', 'quickPrompts.0_4_3'],
  '0.5': ['quickPrompts.0_5_1', 'quickPrompts.0_5_2', 'quickPrompts.0_5_3'],
  '1.1': ['quickPrompts.1_1_1', 'quickPrompts.1_1_2', 'quickPrompts.1_1_3'],
  '1.2': ['quickPrompts.1_2_1', 'quickPrompts.1_2_2', 'quickPrompts.1_2_3'],
  '1.3': ['quickPrompts.1_3_1', 'quickPrompts.1_3_2', 'quickPrompts.1_3_3'],
  '2.1': ['quickPrompts.2_1_1', 'quickPrompts.2_1_2', 'quickPrompts.2_1_3'],
  '2.2': ['quickPrompts.2_2_1', 'quickPrompts.2_2_2', 'quickPrompts.2_2_3'],
  '2.3': ['quickPrompts.2_3_1', 'quickPrompts.2_3_2', 'quickPrompts.2_3_3'],
  '3.1': ['quickPrompts.3_1_1', 'quickPrompts.3_1_2', 'quickPrompts.3_1_3'],
  '3.2': ['quickPrompts.3_2_1', 'quickPrompts.3_2_2', 'quickPrompts.3_2_3'],
  '4.1': ['quickPrompts.4_1_1', 'quickPrompts.4_1_2', 'quickPrompts.4_1_3'],
  '4.2': ['quickPrompts.4_2_1', 'quickPrompts.4_2_2', 'quickPrompts.4_2_3'],
  '5.1': ['quickPrompts.5_1_1', 'quickPrompts.5_1_2', 'quickPrompts.5_1_3'],
  '5.2': ['quickPrompts.5_2_1', 'quickPrompts.5_2_2', 'quickPrompts.5_2_3'],
  'default': ['quickPrompts.default_1', 'quickPrompts.default_2', 'quickPrompts.default_3']
};

const LessonChatPage = () => {
  const { moduleId, lessonId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [module, setModule] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showPrompts, setShowPrompts] = useState(true);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [playingMessageIndex, setPlayingMessageIndex] = useState(null);
  const [generatingAudio, setGeneratingAudio] = useState(null);
  const audioRef = useRef(null);
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);

  // Check if user is Pro
  const isPro = user?.tier === 'pro';

  // Get translated lesson title
  const translatedLessonTitle = useMemo(() => {
    if (!lesson) return t('lesson.thisLesson');
    const key = `lessons.lesson_${lessonId.replace('.', '_')}`;
    const translated = t(key, { defaultValue: '' });
    return translated || lesson.title;
  }, [lesson, lessonId, t]);

  // Get translated module title
  const translatedModuleTitle = useMemo(() => {
    if (!module) return '';
    const key = `modules.module_${moduleId.replace('module_', '')}.title`;
    const translated = t(key, { defaultValue: '' });
    return translated || module.title;
  }, [module, moduleId, t]);

  // Get translated quick prompts for current lesson
  const quickPrompts = useMemo(() => {
    const keys = QUICK_PROMPT_KEYS[lessonId] || QUICK_PROMPT_KEYS['default'];
    return keys.map(key => t(key));
  }, [lessonId, t]);

  useEffect(() => {
    loadLessonAndHistory();
  }, [moduleId, lessonId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const loadLessonAndHistory = async () => {
    try {
      // Load module info
      const moduleRes = await api.getModule(moduleId);
      setModule(moduleRes.data);
      
      // Find the lesson
      const foundLesson = moduleRes.data.lessons?.find(l => l.id === lessonId);
      if (foundLesson) {
        setLesson(foundLesson);
        
        // Track this as the last lesson visited
        try {
          await api.updateLastLesson(moduleId, lessonId, foundLesson.title);
        } catch (err) {
          console.error('Failed to update last lesson:', err);
        }
      }
      
      // Load chat history
      const historyRes = await api.getLessonChatHistory(lessonId);
      
      // Format history into messages
      const historyMessages = [];
      for (const item of historyRes.data.history || []) {
        historyMessages.push({ type: 'user', content: item.user_message });
        historyMessages.push({ type: 'ai', content: item.ai_response });
      }
      
      // If no history, add welcome message in user's language
      if (historyMessages.length === 0) {
        const welcomeMessage = t('lesson.welcomeMessage', { 
          lessonTitle: foundLesson?.title || t('lesson.thisLesson')
        });
        historyMessages.push({
          type: 'ai',
          content: welcomeMessage
        });
      }
      
      setMessages(historyMessages);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Upgrade to access this lesson');
        navigate('/curriculum');
      } else {
        toast.error('Could not load lesson');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || sending) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { type: 'user', content: userMessage }]);
    setSending(true);
    setShowPrompts(false);

    try {
      const response = await api.sendLessonChatMessage(lessonId, userMessage);
      setMessages(prev => [...prev, { type: 'ai', content: response.data.response }]);
    } catch (error) {
      toast.error('Could not get response');
      setMessages(prev => [...prev, { 
        type: 'ai', 
        content: "I'm having trouble connecting right now. Please try again in a moment." 
      }]);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleQuickPrompt = (prompt) => {
    setInput(prompt);
    setShowPrompts(false);
    // Auto-send the prompt
    setTimeout(() => {
      const fakeEvent = { target: { value: prompt } };
      handleSendWithMessage(prompt);
    }, 100);
  };

  const handleSendWithMessage = async (message) => {
    if (!message.trim() || sending) return;

    setInput('');
    setMessages(prev => [...prev, { type: 'user', content: message }]);
    setSending(true);
    setShowPrompts(false);

    try {
      const response = await api.sendLessonChatMessage(lessonId, message);
      setMessages(prev => [...prev, { type: 'ai', content: response.data.response }]);
    } catch (error) {
      toast.error('Could not get response');
      setMessages(prev => [...prev, { 
        type: 'ai', 
        content: "I'm having trouble connecting right now. Please try again in a moment." 
      }]);
    } finally {
      setSending(false);
    }
  };

  // Reset conversation to start fresh in current language
  const handleResetChat = async () => {
    setResetting(true);
    try {
      await api.clearLessonChatHistory(lessonId);
      
      // Set fresh welcome message in current language
      const welcomeMessage = t('lesson.welcomeMessage', { 
        lessonTitle: translatedLessonTitle
      });
      setMessages([{ type: 'ai', content: welcomeMessage }]);
      setShowPrompts(true);
      setShowResetDialog(false);
      toast.success(t('lesson.chatReset'));
    } catch (error) {
      toast.error('Could not reset conversation');
    } finally {
      setResetting(false);
    }
  };

  // Download lesson for offline access (Pro only)
  const handleDownloadLesson = () => {
    if (!isPro) {
      toast.error(t('lesson.offlineProOnly'));
      return;
    }
    
    // Create lesson data for offline storage
    const lessonData = {
      moduleId,
      lessonId,
      lesson: {
        title: translatedLessonTitle,
        moduleTitle: translatedModuleTitle
      },
      messages,
      quickPrompts,
      savedAt: new Date().toISOString(),
      language: user?.preferred_language || 'en'
    };
    
    // Save to localStorage for offline access
    const offlineLessons = JSON.parse(localStorage.getItem('minnow_offline_lessons') || '{}');
    offlineLessons[lessonId] = lessonData;
    localStorage.setItem('minnow_offline_lessons', JSON.stringify(offlineLessons));
    
    toast.success(t('lesson.downloadLessonSuccess'));
  };

  // Voice narration - generate and play audio for AI messages
  const handlePlayMessage = async (messageContent, messageIndex) => {
    // If already playing this message, pause it
    if (playingMessageIndex === messageIndex && audioRef.current) {
      audioRef.current.pause();
      setPlayingMessageIndex(null);
      return;
    }

    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    setGeneratingAudio(messageIndex);

    try {
      // Generate TTS audio
      const response = await api.generateTTS(messageContent, 'nova', 1.0);
      const audioUrl = api.getTTSAudioUrl(response.data.audio_id);
      
      // Create and play audio
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      
      audio.onended = () => {
        setPlayingMessageIndex(null);
        audioRef.current = null;
      };
      
      audio.onerror = () => {
        toast.error(t('lesson.audioError', 'Could not play audio'));
        setPlayingMessageIndex(null);
        audioRef.current = null;
      };
      
      await audio.play();
      setPlayingMessageIndex(messageIndex);
    } catch (error) {
      console.error('TTS error:', error);
      toast.error(t('lesson.audioError', 'Could not generate audio'));
    } finally {
      setGeneratingAudio(null);
    }
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-ocean-blue" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Reset Conversation Dialog */}
      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('lesson.resetChat')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('lesson.resetChatConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleResetChat} disabled={resetting}>
              {resetting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {t('lesson.resetChat')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Header */}
      <header className="flex-shrink-0 bg-ocean-blue text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate(`/curriculum/${moduleId}`)}
                className="text-white hover:bg-white/10"
                data-testid="back-to-module"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <div className="flex items-center gap-2">
                  <GraduationCap className="w-5 h-5" />
                  <span className="font-bold text-sm sm:text-base line-clamp-1">
                    {translatedLessonTitle}
                  </span>
                </div>
                <p className="text-xs text-white/70">{translatedModuleTitle}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="bg-white/20 text-white border-0 hidden sm:flex">
                <MessageCircle className="w-3 h-3 mr-1" />
                {t('lesson.chatBadge')}
              </Badge>
              {/* Options Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" data-testid="lesson-options-menu">
                    <MoreVertical className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => setShowResetDialog(true)} data-testid="reset-chat-btn">
                    <RotateCcw className="w-4 h-4 mr-2" />
                    {t('lesson.resetChat')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleDownloadLesson} 
                    className={!isPro ? 'opacity-50' : ''}
                    data-testid="download-lesson-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {t('lesson.downloadLesson')}
                    {!isPro && <Badge variant="outline" className="ml-2 text-xs">Pro</Badge>}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
        
        {/* Emotional Tone Header */}
        {module?.emotional_tone && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/10 backdrop-blur-sm border-t border-white/10"
            data-testid="emotional-tone-header"
          >
            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-2.5">
              <div className="flex items-center gap-2">
                <Heart className="w-4 h-4 text-coral flex-shrink-0" />
                <p className="text-sm text-white/90 italic">
                  "{module.emotional_tone}"
                </p>
              </div>
            </div>
          </motion.div>
        )}
        
        <div className="wave-divider h-6" />
      </header>

      {/* Chat Area */}
      <ScrollArea 
        className="flex-1 px-4 sm:px-6 py-4"
        ref={scrollRef}
      >
        <div className="max-w-3xl mx-auto space-y-4">
          {/* Learning Objectives Card */}
          {messages.length <= 2 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6"
            >
              <Card className="rounded-2xl border-ocean-blue/20 bg-ocean-blue/5">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-ocean-blue" />
                    <span className="text-sm font-medium text-ocean-blue">{t('lesson.learningFocus')}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t('lesson.learningFocusDesc')}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Messages */}
          {messages.map((message, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {message.type === 'ai' && (
                <div className="w-10 h-10 rounded-xl gradient-ocean flex items-center justify-center mr-3 flex-shrink-0">
                  <Brain className="w-5 h-5 text-white" />
                </div>
              )}
              <div
                className={`max-w-[80%] rounded-2xl ${
                  message.type === 'user'
                    ? 'gradient-ocean text-white p-4'
                    : 'bg-muted'
                }`}
              >
                <div className={message.type === 'ai' ? 'p-4' : ''}>
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">
                    {message.content}
                  </p>
                </div>
                {/* Voice narration button for AI messages */}
                {message.type === 'ai' && (
                  <div className="px-4 pb-2 flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handlePlayMessage(message.content, index)}
                      disabled={generatingAudio === index}
                      className="h-7 px-2 text-xs text-muted-foreground hover:text-ocean-blue"
                      data-testid={`play-message-${index}`}
                    >
                      {generatingAudio === index ? (
                        <Loader2 className="w-3 h-3 animate-spin mr-1" />
                      ) : playingMessageIndex === index ? (
                        <Pause className="w-3 h-3 mr-1" />
                      ) : (
                        <Volume2 className="w-3 h-3 mr-1" />
                      )}
                      {generatingAudio === index 
                        ? t('lesson.generating', 'Generating...') 
                        : playingMessageIndex === index 
                          ? t('lesson.pause', 'Pause') 
                          : t('lesson.listen', 'Listen')}
                    </Button>
                  </div>
                )}
              </div>
            </motion.div>
          ))}

          {/* Sending indicator */}
          {sending && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex justify-start"
            >
              <div className="w-10 h-10 rounded-xl gradient-ocean flex items-center justify-center mr-3">
                <Brain className="w-5 h-5 text-white" />
              </div>
              <div className="bg-muted p-4 rounded-2xl">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-ocean-blue rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-ocean-blue rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-ocean-blue rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </ScrollArea>

      {/* Input Area */}
      <div className="flex-shrink-0 border-t border-border bg-background px-4 sm:px-6 py-4">
        <div className="max-w-3xl mx-auto">
          {/* Quick Answer Prompts */}
          {showPrompts && messages.length <= 2 && !sending && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4"
            >
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb className="w-4 h-4 text-coral" />
                <span className="text-xs font-medium text-muted-foreground">{t('lesson.quickStart')}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {quickPrompts.map((prompt, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuickPrompt(prompt)}
                    className="rounded-full text-xs border-ocean-blue/30 text-ocean-blue hover:bg-ocean-blue/10 hover:border-ocean-blue"
                    data-testid={`quick-prompt-${index}`}
                  >
                    {prompt}
                  </Button>
                ))}
              </div>
            </motion.div>
          )}

          <div className="flex gap-3">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('lesson.inputPlaceholder')}
              className="flex-1 min-h-[48px] max-h-32 resize-none rounded-xl border-2 focus:border-ocean-blue"
              disabled={sending}
              data-testid="lesson-chat-input"
            />
            <Button
              onClick={handleSend}
              disabled={!input.trim() || sending}
              className="h-12 w-12 rounded-xl gradient-ocean text-white"
              data-testid="lesson-chat-send"
            >
              <Send className="w-5 h-5" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2 text-center">
            {t('lesson.enterHint')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LessonChatPage;
